import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../../Spinner';
import {APIClient} from '../../../../utils/APIClient';
import {API_URL} from '../../../../settings/local_settings';
import {ArrowsClockwise, CheckCircle, DownloadSimple} from '../../../icons';
import MarkPaidButton from '../MarkPaidButton';
import {CouponStatusCell} from '../BillingHistorySection';
import {
    NS,
    formatAmount,
    formatLongDate,
    formatShortDate,
    hasUnpaid,
    isCouponMethod,
    lastChargeOf,
    liquidationAmountOf,
    liquidationStateOf,
    nextChargeOf,
    payableCouponOf,
    periodLabelOf,
    splitLiquidations,
} from './subscriptionPageModel';
import {LinkButton, Pill} from './SubscriptionParts';
import {recordSubscriptionEvent} from './subscriptionRequests';

const RAW = {escapeValue: false};

/**
 * «Pagos y facturas» (mockups 1 y 5). El estado de cada fila sale de `display_state` (Tesla); sin él, la
 * regla de siempre. A 390 px la tabla se vuelve tarjetas por CSS (cada celda lleva su rótulo en
 * `data-label`), sin desplazamiento horizontal.
 *
 * El superadmin conserva sus herramientas (la columna del cupón y «Marcar como pago»); sólo él.
 */
export function PaymentsCard({billingData, methodKey, paymentSubscription, isSuperAdmin,
                                 refreshing, onRetry, onRefresh, onNotify, onLiquidationUpdated}) {
    const {t, i18n} = useTranslation();
    const lang = i18n && i18n.language;
    const [expanded, setExpanded] = useState(false);

    const header = (extra) => (
        <div className="sp-card__head">
            <h2 id="sp-payments-title" className="sp-card__title">{t(`${NS}.payments.title`)}</h2>
            {extra}
        </div>
    );

    if (!billingData) {
        return (
            <section className="sp-card sp-payments" aria-labelledby="sp-payments-title" aria-busy="true">
                {header()}
                <div className="box-loader sp-loader"><Spinner color="positive small"/></div>
            </section>
        );
    }

    if (!billingData.available) {
        return (
            <section className="sp-card sp-payments" aria-labelledby="sp-payments-title">
                {header()}
                {billingData.reason === 'tesla_error' ? (
                    <div className="sp-empty">
                        <p>{t(`${NS}.payments.load_error`)}</p>
                        <div className="sp-actions">
                            <button type="button" className="btn -alt -negative" onClick={onRetry}>
                                {t(`${NS}.payments.load_error_retry`)}
                            </button>
                        </div>
                    </div>
                ) : (
                    <p className="sp-empty">{t(`${NS}.payments.empty`)}</p>
                )}
            </section>
        );
    }

    const liquidations = billingData.liquidations || [];
    const unpaid = hasUnpaid(liquidations);
    const {visible, hidden} = splitLiquidations(liquidations);
    const rows = expanded ? liquidations : visible;
    const showCoupons = isSuperAdmin && isCouponMethod(methodKey);

    const refreshButton = unpaid && onRefresh && (
        <LinkButton onClick={onRefresh} disabled={refreshing} className="sp-refresh">
            <ArrowsClockwise size={15} aria-hidden="true" className={refreshing ? '-spin' : ''}/>
            {refreshing ? t(`${NS}.payments.refreshing`) : t(`${NS}.payments.refresh`)}
        </LinkButton>
    );

    return (
        <section className="sp-card sp-payments" aria-labelledby="sp-payments-title">
            {header(refreshButton)}
            {liquidations.length === 0 ? (
                <p className="sp-empty">{t(`${NS}.payments.empty`)}</p>
            ) : (
                <>
                    {!unpaid && <AllGood paymentSubscription={paymentSubscription} lang={lang}/>}
                    <table className={`sp-table${showCoupons ? ' -coupons' : ''}`}>
                        <thead>
                            <tr>
                                <th scope="col">{t(`${NS}.payments.col_period`)}</th>
                                <th scope="col" className="-num">{t(`${NS}.payments.col_amount`)}</th>
                                <th scope="col">{t(`${NS}.payments.col_state`)}</th>
                                {showCoupons && <th scope="col">{t('agency_subscription.coupon_status')}</th>}
                                <th scope="col">{t(`${NS}.payments.col_invoice`)}</th>
                                <th scope="col" className="-num">{t(`${NS}.payments.col_action`)}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((liquidation) => (
                                <LiquidationRow key={liquidation.id} liquidation={liquidation} lang={lang}
                                                showCoupons={showCoupons} isSuperAdmin={isSuperAdmin}
                                                onNotify={onNotify}
                                                onLiquidationUpdated={onLiquidationUpdated}/>
                            ))}
                        </tbody>
                    </table>
                    {!expanded && hidden.length > 0 && (
                        <LinkButton className="sp-more" onClick={() => setExpanded(true)}>
                            {t(`${NS}.payments.see_more`, {count: hidden.length})}
                        </LinkButton>
                    )}
                </>
            )}
        </section>
    );
}

/** «Todo al día», con el último cobro y el próximo si el proveedor los dio. */
function AllGood({paymentSubscription, lang}) {
    const {t} = useTranslation();
    const subscription = paymentSubscription && paymentSubscription.data;
    const last = lastChargeOf(subscription);
    const next = nextChargeOf(subscription);
    const details = [];
    if (last) {
        details.push(last.amount !== null && last.amount !== undefined
            ? t(`${NS}.payments.last_charge`, {
                date: formatLongDate(last.date, lang),
                amount: formatAmount(last.amount, last.currency, lang),
                interpolation: RAW,
            })
            : formatLongDate(last.date, lang));
    }
    if (next) details.push(t(`${NS}.payments.next_charge`, {date: formatLongDate(next.date, lang), interpolation: RAW}));

    return (
        <div className="sp-allgood">
            <CheckCircle size={22} weight="bold" aria-hidden="true"/>
            <div>
                <p className="sp-allgood__title">{t(`${NS}.payments.all_good`)}</p>
                {details.length > 0 && <p className="sp-allgood__detail">{details.join(' · ')}</p>}
            </div>
        </div>
    );
}

function StateCell({liquidation, lang}) {
    const {t} = useTranslation();
    const state = liquidationStateOf(liquidation);
    let pill;
    if (state.kind === 'paid') {
        pill = <Pill tone="ok">{state.date
            ? t(`${NS}.payments.paid_on`, {date: formatShortDate(state.date, lang), interpolation: RAW})
            : t(`${NS}.payments.paid`)}</Pill>;
    } else if (state.kind === 'overdue') {
        pill = <Pill tone="bad">{state.days !== null
            ? t(`${NS}.payments.overdue_days`, {count: state.days})
            : t(`${NS}.payments.overdue`)}</Pill>;
    } else {
        pill = <Pill tone="warn">{state.dueDate
            ? t(`${NS}.payments.due_on`, {date: formatShortDate(state.dueDate, lang), interpolation: RAW})
            : t(`${NS}.payments.pending`)}</Pill>;
    }
    const notification = liquidation.payment_notification;
    return (
        <>
            {pill}
            {/* Sólo un aviso que nadie resolvió todavía: uno rechazado no es «aviso enviado» (C-sp-fe-05). */}
            {state.kind !== 'paid' && notification && notification.status === 'PENDING' && notification.created_at && (
                <span className="sp-table__note">
                    {t(`${NS}.payments.notified`, {date: formatShortDate(notification.created_at, lang), interpolation: RAW})}
                </span>
            )}
        </>
    );
}

function downloadInvoice(t, invoice) {
    recordSubscriptionEvent('invoice_download');
    const url = `${API_URL}agency/subscription/invoice/${invoice.colppy_invoice_id}/download/`;
    const fallbackName = `factura-${invoice.number || invoice.colppy_invoice_id}.pdf`;
    new APIClient(url).download('GET', fallbackName, (result) => {
        if (result && result.status && result.status !== 'OK') {
            toast.error(t('agency_subscription.download_invoice.toast_error'));
        }
    });
}

function InvoiceCell({liquidation}) {
    const {t} = useTranslation();
    const invoices = liquidation.invoices || [];
    if (invoices.length === 0) {
        return <span className="sp-muted">{liquidation.colppy_invoice_numbers || t(`${NS}.payments.no_invoice`)}</span>;
    }
    return (
        <div className="sp-invoices">
            {invoices.map((invoice) => {
                const number = invoice.number || invoice.colppy_invoice_id;
                return (
                    <LinkButton key={invoice.colppy_invoice_id} onClick={() => downloadInvoice(t, invoice)}
                                aria-label={t(`${NS}.payments.invoice_aria`, {number, interpolation: RAW})}>
                        <DownloadSimple size={14} aria-hidden="true"/>
                        {t(`${NS}.payments.invoice_pdf`, {number, interpolation: RAW})}
                    </LinkButton>
                );
            })}
        </div>
    );
}

function LiquidationRow({liquidation, lang, showCoupons, isSuperAdmin, onNotify, onLiquidationUpdated}) {
    const {t} = useTranslation();
    const {amount, currency} = liquidationAmountOf(liquidation);
    const state = liquidationStateOf(liquidation);
    const unpaid = state.kind !== 'paid';
    const coupon = unpaid ? payableCouponOf(liquidation) : null;
    const label = (key) => t(`${NS}.payments.${key}`);

    return (
        <tr className={state.kind === 'overdue' ? '-overdue' : undefined}>
            <td data-label={label('col_period')} className="sp-table__period">
                {periodLabelOf(liquidation, lang)}
            </td>
            <td data-label={label('col_amount')} className="-num -strong">
                {formatAmount(amount, currency, lang, {decimals: true}) || '—'}
            </td>
            <td data-label={label('col_state')}><StateCell liquidation={liquidation} lang={lang}/></td>
            {showCoupons && (
                <td data-label={t('agency_subscription.coupon_status')}>
                    <CouponStatusCell cupons={liquidation.payment_cupons}/>
                </td>
            )}
            <td data-label={label('col_invoice')}><InvoiceCell liquidation={liquidation}/></td>
            <td data-label={label('col_action')} className="-num">
                {unpaid && (
                    <div className="sp-row-actions">
                        {isSuperAdmin && <MarkPaidButton liquidationId={liquidation.id} onMarked={onLiquidationUpdated}/>}
                        {coupon && (
                            <a className={isSuperAdmin ? 'sp-link' : 'btn -alt sp-pay'} href={coupon.payment_url}
                               target="_blank" rel="noopener noreferrer"
                               onClick={() => recordSubscriptionEvent('pay_click')}>
                                {label('pay')}
                            </a>
                        )}
                        {!isSuperAdmin && (
                            <LinkButton onClick={() => onNotify(liquidation)}>{label('i_paid')}</LinkButton>
                        )}
                    </div>
                )}
            </td>
        </tr>
    );
}
